import type { FC } from "react";
import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { useTranslations } from "@ui/providers/useTranslations";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import type { AlertBannerAction, AlertBannerProps } from "@jti/core-components/components/AlertBanner";
import { AlertBanner } from "@jti/core-components/components/AlertBanner";

import { AgeVerificationDialog } from "../AgeVerificationDialog";
import type { AvUserDataType } from "../ValidationMethods";

import styles from "./AgeVerificationBanner.module.scss";

const { sanitize } = dompurify;

export type AgeVerificationBannerProps = {
  dialogDescription: string;
  successMessage: string;
  isPending: boolean;
  userData?: AvUserDataType;
  onSuccess?: (avpUuid: string) => void;
  mode?: "popup" | "link";
  isAgeVerificationSuccess?: boolean;
  ageVerificationMethodImages?: Record<string, string>;
};

export const AgeVerificationBanner: FC<AgeVerificationBannerProps> = ({
  onSuccess,
  userData,
  dialogDescription,
  successMessage,
  isPending,
  mode = "link",
  isAgeVerificationSuccess = false,
  ageVerificationMethodImages,
}) => {
  const { PROFILE_PAGE_URL } = useConfig();
  const { __ } = useTranslations();
  const history = useHistory();
  const { search } = useLocation();
  const currentPageUrl = encodeURIComponent(window.location.href);
  const ageVerificationLink = `${window.location.origin}${PROFILE_PAGE_URL}#/age-verification?returnUrl=${currentPageUrl}`;
  const [isAgeVerificationOpened, setIsAgeVerificationOpened] = useState(false);

  const handleSuccess = (avpUuid?: string) => {
    setIsAgeVerificationOpened(false);
    if (avpUuid) onSuccess?.(avpUuid);
  };

  const onLoadDataFromCache = () => {
    setIsAgeVerificationOpened(true);
  };

  const primaryButton: AlertBannerAction | undefined =
    !isAgeVerificationSuccess && mode === "popup"
      ? {
          label: __("libs.ui.notification.limit.age_verification_link"),
          onClick: () => setIsAgeVerificationOpened(true),
          disabled: isPending,
          ["data-testid"]: "ageVerificationBannerButton",
        }
      : undefined;

  const ctaButton: AlertBannerAction | undefined =
    mode === "link"
      ? {
          label: __("libs.ui.notification.limit.age_verification_link"),
          href: ageVerificationLink,
          "data-testid": "ageVerificationLink",
        }
      : undefined;

  const actions = [primaryButton, ctaButton].filter((action): action is AlertBannerAction => Boolean(action));

  return (
    <div className={styles.container}>
      <div className={styles.messageBox}>
        {isAgeVerificationSuccess ? (
          <AlertBanner
            tone="success"
            actions={actions as AlertBannerProps["actions"]}
            richDescription={<div dangerouslySetInnerHTML={{ __html: sanitize(successMessage) }}></div>}
          />
        ) : (
          <AlertBanner
            tone="warning"
            title={__("libs.ui.notification.limit.age_verification_title")}
            actions={actions as AlertBannerProps["actions"]}
            description={__("libs.ui.notification.limit.age_verification")}
          />
        )}
      </div>
      <AgeVerificationDialog
        isOpened={isAgeVerificationOpened}
        onLoadDataFromCache={onLoadDataFromCache}
        description={dialogDescription}
        userData={userData}
        onClose={() => setIsAgeVerificationOpened(false)}
        onSuccess={handleSuccess}
        ageVerificationMethodImages={ageVerificationMethodImages}
        navigation={{
          search,
          replaceSearch: (nextSearch) => history.replace({ search: nextSearch }),
        }}
      />
    </div>
  );
};
